import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronRight, Mic, MicOff, Volume2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { Button } from '../../components/ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'
import { useEnglishVocab } from '../../hooks/useEnglishVocab'
import { useSpeechRecognition, pronunciationSimilarity } from '../../hooks/useSpeechRecognition'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { cn } from '../../lib/utils'

// Score threshold buckets — controls the tone of the result card.
function bucketForScore(score) {
  if (score >= 85) return 'great'
  if (score >= 60) return 'ok'
  return 'retry'
}

// Persist each attempt so future weekly insights / per-vocab history
// can read them. Best-effort — failure doesn't block the UI flow.
async function logAttempt({ userId, vocabId, target, transcript, score }) {
  if (!userId) return
  try {
    await supabase.from('english_pronunciation_attempts').insert({
      user_id: userId,
      vocab_id: vocabId,
      target_text: target,
      transcript: transcript || null,
      score: typeof score === 'number' ? score : null,
    })
  } catch { /* swallow */ }
}

export function EnglishSpeakPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const vocabHook = useEnglishVocab()
  const recog = useSpeechRecognition({ lang: 'en-US' })
  const tts = useTextToSpeech()

  // Pool of drillable items — every active (non-suspended) row that
  // has a word. Reset cursor when the pool length changes (e.g.
  // adding new vocab during a session).
  const pool = useMemo(
    () => (vocabHook.rows || []).filter((v) => v.srs_state !== 'suspended' && (v.word || '').trim().length > 0),
    [vocabHook.rows],
  )
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (index >= pool.length) setIndex(0)
  }, [pool.length, index])
  const current = pool[index] || null

  const [lastResult, setLastResult] = useState(null) // { score, transcript, vocabId }

  // Reset the result panel whenever the user moves to a new card.
  // Pull `reset` out of the recog hook before depending on it:
  // the hook returns a fresh object literal on every render, so
  // depending on `recog` itself made this effect fire every render
  // — which silently wiped the score the moment we set it. The
  // user reported "tap mic, nothing happens" because the result
  // card flashed on for one render and immediately got reset.
  const resetRecog = recog.reset
  useEffect(() => {
    setLastResult(null)
    resetRecog()
  }, [current?.id, resetRecog])

  const targetText = current?.word || ''

  // When recognition finishes (listening flips off and we have a
  // transcript), score + persist + show the result.
  useEffect(() => {
    if (recog.listening) return
    if (!recog.transcript) return
    if (lastResult && lastResult.transcript === recog.transcript) return
    if (!targetText) return
    const score = pronunciationSimilarity(targetText, recog.transcript)
    const next = { score, transcript: recog.transcript, vocabId: current?.id || null }
    setLastResult(next)
    logAttempt({
      userId: user?.id,
      vocabId: current?.id || null,
      target: targetText,
      transcript: recog.transcript,
      score,
    })
  }, [recog.listening, recog.transcript, targetText, current?.id, lastResult, user?.id])

  const handleSpeak = useCallback(() => {
    if (!tts.supported || !targetText) return
    tts.play(targetText, 'en')
  }, [tts, targetText])

  const handleRecord = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[speak] mic button tap supported=' + recog.supported + ' listening=' + recog.listening + ' target="' + targetText + '"')
    if (!recog.supported) return
    if (recog.listening) recog.stop()
    else recog.start()
  }, [recog, targetText])

  const handleNext = useCallback(() => {
    if (pool.length === 0) return
    setIndex((i) => (i + 1) % pool.length)
  }, [pool.length])

  const goBack = () => navigate('/english')

  // Permission-denied + unsupported branches surface as full-page
  // states; everything else renders the drill card.
  const showsUnsupported = !recog.supported

  return (
    <div className="space-y-3 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
            <Mic className="w-4 h-4 text-app-primary-500" />
            {t('english.speak.title')}
          </h1>
          <p className="text-xs text-muted-foreground" dir="auto">
            {t('english.speak.subtitle', { count: pool.length })}
          </p>
        </div>
      </div>

      {showsUnsupported ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <MicOff className="w-4 h-4" />
              <p className="text-sm font-semibold" dir="auto">{t('english.speak.unsupported')}</p>
            </div>
            <p className="text-xs text-muted-foreground" dir="auto">
              {t('english.speak.unsupportedHint')}
            </p>
          </CardContent>
        </Card>
      ) : vocabHook.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : pool.length === 0 ? (
        <EmptyState
          icon={Mic}
          title={t('english.speak.empty')}
          description={t('english.speak.emptyHint')}
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 min-h-[320px] flex flex-col">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" dir="auto">
                    {t('english.speak.targetLabel')}
                  </p>
                  <div className="flex items-baseline gap-2 flex-wrap mt-1" dir="ltr">
                    <h2 className="text-3xl font-bold text-foreground">{current.word}</h2>
                    {current.pronunciation_ipa && (
                      <span className="text-sm text-muted-foreground font-mono">{current.pronunciation_ipa}</span>
                    )}
                  </div>
                  {current.definition_en && (
                    <p className="text-xs text-muted-foreground mt-1.5" dir="ltr">{current.definition_en}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSpeak}
                  aria-label={t('english.speak.listen')}
                  title={t('english.speak.listen')}
                  className="p-2 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent flex-shrink-0"
                >
                  <Volume2 className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1" />

              {/* Mic button — colour reflects state. Tap to start;
                  tap again to stop (or wait for the timeout). */}
              <button
                type="button"
                onClick={handleRecord}
                disabled={!recog.supported}
                aria-label={recog.listening ? t('english.speak.stop') : t('english.speak.record')}
                className={cn(
                  'mx-auto w-20 h-20 rounded-full inline-flex items-center justify-center transition-all',
                  recog.listening
                    ? 'bg-red-500 text-white shadow-lg animate-pulse'
                    : 'bg-app-primary-500 text-white hover:opacity-90 active:scale-95',
                )}
              >
                {recog.listening ? <MicOff className="w-7 h-7" /> : <Mic className="w-7 h-7" />}
              </button>

              <p className="text-center text-xs text-muted-foreground" dir="auto">
                {recog.listening
                  ? t('english.speak.listening')
                  : t('english.speak.tapToRecord')}
              </p>

              {recog.interim && (
                <p className="text-center text-xs text-muted-foreground italic" dir="ltr">
                  &ldquo;{recog.interim}&rdquo;
                </p>
              )}

              {recog.error && !recog.listening && (
                <p className="text-center text-xs text-red-600" dir="auto">
                  {t(`english.speak.errors.${recog.error}`, { defaultValue: recog.error })}
                </p>
              )}
            </CardContent>
          </Card>

          {lastResult && (
            <Card
              className={cn(
                'border',
                bucketForScore(lastResult.score) === 'great' && 'border-emerald-500/40 bg-emerald-500/5',
                bucketForScore(lastResult.score) === 'ok' && 'border-amber-500/40 bg-amber-500/5',
                bucketForScore(lastResult.score) === 'retry' && 'border-red-500/40 bg-red-500/5',
              )}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg',
                    bucketForScore(lastResult.score) === 'great' && 'bg-emerald-500/15 text-emerald-600',
                    bucketForScore(lastResult.score) === 'ok' && 'bg-amber-500/15 text-amber-600',
                    bucketForScore(lastResult.score) === 'retry' && 'bg-red-500/15 text-red-600',
                  )} dir="ltr">
                    {lastResult.score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground" dir="auto">
                      {t(`english.speak.score.${bucketForScore(lastResult.score)}`)}
                    </p>
                    <p className="text-[11px] text-muted-foreground" dir="auto">
                      {t('english.speak.heard')}
                    </p>
                    <p className="text-sm text-foreground italic mt-0.5" dir="ltr">
                      &ldquo;{lastResult.transcript}&rdquo;
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleRecord} className="flex-1">
                    <Mic className="w-3.5 h-3.5 me-1" />
                    {t('english.speak.tryAgain')}
                  </Button>
                  <Button size="sm" onClick={handleNext} className="flex-1">
                    {t('english.speak.next')}
                    <ChevronRight className="w-3.5 h-3.5 ms-1 rtl:-scale-x-100" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!lastResult && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleNext} className="w-full">
                {t('english.speak.skip')}
                <ChevronRight className="w-3.5 h-3.5 ms-1 rtl:-scale-x-100" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
